import { type ReactNode, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { cx } from "../cx";
import styles from "./Tabs.module.css";

export interface TabItem<K extends string = string> {
  key: K;
  label: ReactNode;
  disabled?: boolean;
}

export interface TabsProps<K extends string = string> {
  tabs: ReadonlyArray<TabItem<K>>;
  active: K;
  /** Without `onChange`, tabs render as `#key` links so they work with `useHashTab`. */
  onChange?: (key: K) => void;
  /** `boxed` = cl-nav-tabs (grey boxes, active white); `minimal` = underlined text tabs. */
  variant?: "boxed" | "minimal";
  className?: string;
  "aria-label"?: string;
}

export function Tabs<K extends string>({
  tabs,
  active,
  onChange,
  variant = "boxed",
  className,
  ...rest
}: TabsProps<K>) {
  return (
    <div role="tablist" aria-label={rest["aria-label"]} className={cx(styles.tabs, styles[variant], className)}>
      {tabs.map((t) => {
        const selected = t.key === active;
        const cls = cx(styles.tab, selected && styles.active, t.disabled && styles.disabled);
        if (onChange) {
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={selected}
              disabled={t.disabled}
              className={cls}
              onClick={() => onChange(t.key)}
            >
              {t.label}
            </button>
          );
        }
        return (
          <Link
            key={t.key}
            role="tab"
            aria-selected={selected}
            aria-disabled={t.disabled || undefined}
            className={cls}
            to={{ hash: `#${t.key}` }}
            replace
            preventScrollReset
          >
            {t.label}
          </Link>
        );
      })}
    </div>
  );
}

/**
 * Hash-synced tab state (Clockify's `/teams#members`, `/user/preferences#advanced`).
 * An unknown or empty hash resolves to `fallback` without rewriting the URL.
 */
export function useHashTab<K extends string>(
  keys: ReadonlyArray<K>,
  fallback: K,
): [active: K, setActive: (key: K) => void] {
  const location = useLocation();
  const navigate = useNavigate();
  const raw = decodeURIComponent(location.hash.replace(/^#/, ""));
  const active = (keys as ReadonlyArray<string>).includes(raw) ? (raw as K) : fallback;
  const setActive = useCallback(
    (key: K) => {
      navigate({ pathname: location.pathname, search: location.search, hash: `#${key}` }, { replace: true });
    },
    [navigate, location.pathname, location.search],
  );
  return [active, setActive];
}
